/**
 * Authentication Context for Microsoft Entra ID (Azure AD) SSO
 * 
 * This module provides authentication using Microsoft SSO via MSAL.
 * Users authenticate with their Coveo Microsoft credentials.
 * Admin access is determined by Azure AD group membership.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  AccountInfo,
  InteractionStatus,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { getMsalInstance, loginRequest, isLocalAuthBypassEnabled } from '../config/msal';
import { apiService } from '../services/api';

// Only the explicit, environment-gated local bypass may provide a mock user.
const isDevMode = isLocalAuthBypassEnabled();

interface User {
  email: string;
  name: string;
  sub: string;  // Object ID from Microsoft
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  isDevMode: boolean;
  isDemoEditor: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Dev mode mock user
const DEV_USER: User = {
  email: 'dev@coveo.com',
  name: 'Dev User',
  sub: 'dev-user-123',
};

/**
 * Inner auth provider that uses MSAL hooks
 */
function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  
  const [user, setUser] = useState<User | null>(isDevMode ? DEV_USER : null);
  const [accessToken, setAccessToken] = useState<string | null>(isDevMode ? 'dev-token' : null);
  const [isLoading, setIsLoading] = useState(!isDevMode);
  const [isDemoEditor, setIsDemoEditor] = useState<boolean>(isDevMode);

  // Get the active account
  const activeAccount = accounts[0] || null;

  const clearAuthState = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setIsDemoEditor(false);
    localStorage.removeItem('accessToken');
  }, []);

  // Fetch /api/me to get is_demo_editor status
  const fetchMeInfo = useCallback(async () => {
    try {
      const meResponse = await apiService.getMe();
      setIsDemoEditor(meResponse.is_demo_editor);
    } catch (error) {
      console.warn('Failed to fetch /api/me:', error);
      setIsDemoEditor(false);
    }
  }, []);

  // Acquire token silently
  const acquireToken = useCallback(async (account: AccountInfo): Promise<string | null> => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or requires interaction, redirect to login
        try {
          const response = await instance.acquireTokenPopup(loginRequest);
          return response.idToken;
        } catch (popupError) {
          console.error('Failed to acquire token via popup:', popupError);
          return null;
        }
      }
      console.error('Failed to acquire token silently:', error);
      return null;
    }
  }, [instance]);

  // Check auth state when accounts change
  useEffect(() => {
    if (isDevMode) {
      setUser(DEV_USER);
      setAccessToken('dev-token');
      setIsDemoEditor(true);
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      if (inProgress !== InteractionStatus.None) {
        return; // Wait for MSAL to finish any interaction
      }

      if (activeAccount) {
        // User is authenticated, get token
        const token = await acquireToken(activeAccount);
        
        if (token) {
          instance.setActiveAccount(activeAccount);
          setAccessToken(token);
          localStorage.setItem('accessToken', token);
          
          setUser({
            email: activeAccount.username || '',
            name: activeAccount.name || '',
            sub: activeAccount.localAccountId || '',
          });
          
          // Fetch /api/me to get is_demo_editor status
          await fetchMeInfo();
        } else {
          instance.setActiveAccount(null);
          clearAuthState();
        }
      } else {
        instance.setActiveAccount(null);
        clearAuthState();
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, [activeAccount, inProgress, acquireToken, clearAuthState, fetchMeInfo, instance]);

  const login = async () => {
    if (isDevMode) {
      setUser(DEV_USER);
      setAccessToken('dev-token');
      setIsDemoEditor(true);
      return;
    }

    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    if (isDevMode) {
      clearAuthState();
      return;
    }

    try {
      const accountToLogout = activeAccount ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;

      // Clear local state and cached MSAL state before redirecting so the app
      // cannot silently restore the previous session on the way back.
      clearAuthState();
      instance.setActiveAccount(null);
      await instance.clearCache();

      await instance.logoutRedirect({
        account: accountToLogout,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const refreshSession = async (): Promise<string | null> => {
    if (isDevMode) {
      return 'dev-token';
    }

    if (!activeAccount) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount,
        forceRefresh: true,
      });
      const token = response.idToken;
      instance.setActiveAccount(activeAccount);
      setAccessToken(token);
      localStorage.setItem('accessToken', token);
      return token;
    } catch (error) {
      console.error('Failed to refresh session:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: isDevMode ? !!user : isAuthenticated,
    isLoading: isDevMode ? false : (isLoading || inProgress !== InteractionStatus.None),
    accessToken,
    isDevMode,
    isDemoEditor,
    login,
    logout,
    refreshSession,
  };

  // Log dev mode status
  if (isDevMode) {
    console.log('🔧 Dev Mode Active - Authentication bypassed');
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Main Auth Provider that wraps MSAL Provider
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const msalInstance = getMsalInstance();

  // Local mode deliberately has no MSAL instance. In every other environment,
  // missing Microsoft configuration is a hard authentication failure rather than
  // an implicit mock-user mode.
  if (isDevMode) {
    return (
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    );
  }

  if (!msalInstance) {
    return (
      <div role="alert" className="p-6 text-sm text-rose-700">
        Microsoft SSO is not configured for this environment.
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    </MsalProvider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
